import { URLSearchParamsInit, createSearchParams } from 'react-router-dom';

import { message } from 'antd';
import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import { IUserEntityInitialState } from 'redux/types/UserEntities.types';
import { agentOnboardingTemplateURL } from 'services/api/endPoints';

import {
  ALLOCATOR_GROUP,
  YUCOLLECT_ADMIN_GROUP,
} from '../AgencyOnboarding/Profile/constants';
import { CONDITION_KEYS } from './constant';
import { Column, IAppliedFilters } from './types';

export const ONE_THOUSAND = 1000;
export const ONE_LAKH = 100_000;
export const ONE_CRORE = 100_00_000;

export function formatCurrency(value: number | bigint | null): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value !== 'number' && typeof value !== 'bigint') {
    throw new Error('Input must be a number or bigint');
  }

  const numericValue = typeof value === 'bigint' ? Number(value) : value;

  // Number format options for Indian locale
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });

  // Helper function to format values with suffix
  function formatWithSuffix(value: number, suffix: string): string {
    return `${formatter.format(value)} ${suffix}`;
  }

  // Handle different magnitudes and append the appropriate suffix
  if (numericValue >= ONE_CRORE) {
    // Calculate the value in crores
    return formatWithSuffix(numericValue / ONE_CRORE, 'Cr');
  } else if (numericValue >= ONE_LAKH) {
    // Calculate the value in lakhs
    return formatWithSuffix(numericValue / ONE_LAKH, 'Lakh');
  } else {
    // Format the number without lakh or crore suffix
    return formatter.format(numericValue);
  }
}

// Utility function to mask the loan application number
export function maskLoanApplicationNo(value: string) {
  if (value.length <= 4) return value;
  return 'XXXX' + value.slice(-4);
}

export type ConditionKey = (typeof CONDITION_KEYS)[number];

interface ConditionMapping {
  [key: string]: string;
}

export const CONDITION_MAPPING: ConditionMapping = {
  isEqualTo: '=',
  isLessThan: '<',
  isGreaterThan: '>',
  isLessThanOrEqualTo: '≤',
  isGreaterThanOrEqualTo: '≥',
  notEqual: '≠',
  between: 'BETWEEN',
  none: 'NONE',
};

export function formatFilter(
  conditionKey: ConditionKey,
  value: string | number | [string | number, string | number]
): { symbol: string; value: string | number; symbolDesc: string } {
  const symbol = CONDITION_MAPPING[conditionKey];

  const valueAsArray =
    conditionKey === 'between' ? value.toString().split('|') : value;

  switch (conditionKey) {
    case 'isEqualTo':
    case 'isLessThan':
    case 'isGreaterThan':
    case 'isLessThanOrEqualTo':
    case 'isGreaterThanOrEqualTo':
    case 'notEqual':
      return { symbol, value: value.toString(), symbolDesc: conditionKey };

    case 'between':
      if (Array.isArray(valueAsArray) && valueAsArray.length === 2) {
        return {
          symbol,
          value: `${valueAsArray[0]} AND ${valueAsArray[1]}`,
          symbolDesc: conditionKey,
        };
      }
      throw new Error('Value for "between" must be an array of two items');

    case 'none':
      return {
        symbol: '',
        value: 'No filter applied',
        symbolDesc: conditionKey,
      };

    default:
      throw new Error('Invalid condition');
  }
}

export function transformFiltersToPayload(filterConfig: {
  [key: string]: any;
}) {
  return Object.entries(filterConfig).map(([columnKey, columnConfig]) => {
    const {
      sort = '',
      filterByValues = [],
      filterByConditions = {},
    } = columnConfig || {};

    const cleanedFilterByConditions =
      Object.keys(filterByConditions).length > 0 && 'none' in filterByConditions
        ? {}
        : filterByConditions;

    return {
      name: columnKey,
      sort,
      filterByValues,
      filterByConditions: cleanedFilterByConditions,
    };
  });
}

// Function to transform data
export const transformPayloadToFilters = (columns: Column[]) => {
  const result: IAppliedFilters = {};

  columns.forEach((column) => {
    const { name, sort, filterByValues, filterByConditions } = column;

    // Combine filter conditions
    const existingConditions = result[name]?.filterByConditions || {};
    const combinedConditions = { ...existingConditions, ...filterByConditions };

    result[name] = {
      filterByConditions: combinedConditions,
      filterByValues: filterByValues || [],
      sort: sort || '',
    };
  });

  return result;
};

export function formatIndianNumber(number: number | null) {
  if (number === null || number === undefined) return 'N/A';
  const formatter = new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
  });

  return formatter.format(number);
}

export const formatPercentage = (
  value: number | null,
  precision = 2
): string => {
  if (value === null || value === undefined) return 'N/A';
  if (isNaN(value)) return `0%`;
  return `${value.toFixed(precision)}%`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

/**
 * Formats a chat message timestamp for display (e.g. "07:50 AM").
 * Handles "YYYY-MM-DD HH:mm:ss", ISO strings, and Unix timestamps (seconds or ms).
 */
export const formatChatTimestamp = (timestamp: string | number): string => {
  if (!timestamp) return '';
  const parsed = dayjs(
    typeof timestamp === 'number'
      ? timestamp
      : String(timestamp).replace(' ', 'T')
  );
  return parsed.isValid() ? parsed.format('hh:mm A') : '';
};

export const formatKey = (key: string) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
};

export interface IDropdownItem {
  label: string;
  value: string;
  id: string | number;
}

interface IBaseItem {
  name: string;
  id: string | number;
  [key: string]: any;
}

export const formatToDropdownOptions = <T extends IBaseItem>(
  items: T[],
  labelKey: keyof T = 'name' as keyof T,
  valueKey?: keyof T,
  idKey: keyof T = 'id' as keyof T
): IDropdownItem[] => {
  return items.map((item) => ({
    key: item[idKey],
    label: String(item[labelKey]),
    value: String(item[valueKey ?? labelKey]),
    id: item[idKey],
  }));
};

export function detectChannelPlannerName(route: string) {
  return route.includes('field') ? 'Field' : 'Call';
}

export function formatAmount(amountVal: string) {
  return Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(
    Number(amountVal)
  );
}

export const getOptimizedChannelPayload = (
  selectedIds: string[],
  unselectedIds: string[],
  totalRecords: number
) => {
  // Case 1: If selected IDs equal total records, send empty arrays
  if (selectedIds.length === totalRecords) {
    return {
      selectedRecords: [],
      unselectedRecords: [],
      isAllSelected: true,
    };
  }

  // Case 2: If selected IDs count is greater than unselected IDs count
  if (selectedIds.length > unselectedIds.length || unselectedIds.length !== 0) {
    return {
      selectedRecords: selectedIds,
      unselectedRecords: unselectedIds,
      isAllSelected: true,
    };
  }

  // Case 3: If selected IDs count is less than unselected IDs count
  if (selectedIds.length <= unselectedIds.length && selectedIds.length !== 0) {
    return {
      selectedRecords: selectedIds,
      unselectedRecords: [],
      isAllSelected: false,
    };
  }

  // Default case: return original arrays
  return {
    selectedRecords: selectedIds,
    unselectedRecords: unselectedIds,
    isAllSelected: false,
  };
};

/**
 * V2 - Simplified channel payload generation based on user action tracking
 * Avoids the need for totalRecords/distinct API calls by tracking actual user actions
 *
 * @param selectedIds - Array of IDs that are currently selected
 * @param unselectedIds - Array of IDs that were unselected from a "select all" state
 * @param isSelectAllActive - Boolean indicating if "select all" was clicked
 * @returns Optimized payload for API calls
 */
export const getOptimizedChannelPayloadV2 = ({
  selectedIds,
  unselectedIds,
  isSelectAllActive,
}: {
  selectedIds: string[];
  unselectedIds: string[];
  isSelectAllActive: boolean;
}) => {
  // Case 1: Select All was clicked - send selected IDs, empty unselected
  if (isSelectAllActive) {
    return {
      selectedRecords: selectedIds,
      unselectedRecords: [],
      isAllSelected: true,
    };
  }

  // Case 2: Individual selection/unselection
  // If user selected individual items, send selectedIds
  // If user unselected items (from select all), send unselectedIds
  if (unselectedIds.length > 0) {
    // User unselected some items from select all state
    return {
      selectedRecords: [],
      unselectedRecords: unselectedIds,
      isAllSelected: false,
    };
  } else {
    // User selected individual items
    return {
      selectedRecords: selectedIds,
      unselectedRecords: [],
      isAllSelected: false,
    };
  }
};

/**
 * V2 - Helper type for tracking selection state without needing total record count
 */
export interface SelectionStateV2 {
  selectedIds: string[];
  unselectedIds: string[];
  isSelectAllActive: boolean;
  selectionMode: 'individual' | 'select_all' | 'unselect_from_all';
}

/**
 * V2 - Helper function to manage selection state transitions
 */
export const updateSelectionStateV2 = (
  currentState: SelectionStateV2,
  action: {
    type:
      | 'SELECT_ALL'
      | 'UNSELECT_ALL'
      | 'SELECT_INDIVIDUAL'
      | 'UNSELECT_INDIVIDUAL';
    ids?: string[];
  }
): SelectionStateV2 => {
  switch (action.type) {
    case 'SELECT_ALL':
      return {
        selectedIds: [],
        unselectedIds: [],
        isSelectAllActive: true,
        selectionMode: 'select_all',
      };

    case 'UNSELECT_ALL':
      return {
        selectedIds: [],
        unselectedIds: [],
        isSelectAllActive: false,
        selectionMode: 'individual',
      };

    case 'SELECT_INDIVIDUAL':
      if (currentState.isSelectAllActive) {
        // If select all is active and user selects more, maintain select all state
        return currentState;
      } else if (currentState.selectionMode === 'unselect_from_all') {
        // If in unselect_from_all mode, remove from unselected list (re-selecting)
        const idsToReselect = new Set(action.ids || []);
        const newUnselectedIds = currentState.unselectedIds.filter(
          (id) => !idsToReselect.has(id)
        );
        return {
          ...currentState,
          unselectedIds: newUnselectedIds,
          selectionMode: 'unselect_from_all', // Stay in unselect_from_all mode
        };
      } else {
        // Add to selected IDs for individual selection
        const newSelectedIds = [
          ...new Set([...currentState.selectedIds, ...(action.ids || [])]),
        ];
        return {
          ...currentState,
          selectedIds: newSelectedIds,
          selectionMode: 'individual',
        };
      }

    case 'UNSELECT_INDIVIDUAL':
      if (
        currentState.isSelectAllActive ||
        currentState.selectionMode === 'unselect_from_all'
      ) {
        // If select all is active OR already in unselect_from_all mode, add to unselected list
        return {
          selectedIds: [],
          unselectedIds: [
            ...new Set([...currentState.unselectedIds, ...(action.ids || [])]),
          ],
          isSelectAllActive: false,
          selectionMode: 'unselect_from_all',
        };
      } else {
        // Remove from selected IDs for individual unselection
        const idsToRemove = new Set(action.ids || []);
        const newSelectedIds = currentState.selectedIds.filter(
          (id) => !idsToRemove.has(id)
        );
        return {
          ...currentState,
          selectedIds: newSelectedIds,
          selectionMode: 'individual',
        };
      }

    default:
      return currentState;
  }
};

export function getUserDetails(
  userEntities: IUserEntityInitialState
): Record<string, string> {
  if (
    !userEntities ||
    userEntities.status !== 'SUCCESS' ||
    !userEntities.userData
  ) {
    return { error: 'Invalid or unsuccessful userEntities object.' };
  }

  const { userData } = userEntities;

  return {
    email: userData.email || '',
    name: userData.name || '',
    userId: userData.ca_user_id || '',
    entityId: userData.default_entity || '',
    companyName: userData.user_entities?.[0]?.company_name || '',
    userGroup: userData.user_entities?.[0]?.groups?.[0]?.group_code || '',
    userRole: userData.user_entities?.[0]?.groups?.[0]?.roles?.[0]?.name || '',
  };
}

export function downloadExcelFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function downloadTemplate(url?: string, filename?: string) {
  const templateUrl = url || agentOnboardingTemplateURL;
  const templateFilename = filename || 'Agent Onboarding Template.xlsx';

  const link = document.createElement('a');
  link.href = templateUrl;
  link.setAttribute('download', templateFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const secretKey = 'your-secret-key-12345678'; // Must match backend
const iv = CryptoJS.enc.Utf8.parse('your-iv-12345678'); // Must be 16 bytes

export const encryptPhoneNumber = (phoneNumber: string) => {
  const encrypted = CryptoJS.AES.encrypt(
    phoneNumber,
    CryptoJS.enc.Utf8.parse(secretKey),
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  );
  return encrypted.toString();
};

export const getOptimizedAssignAgentsPayload = (
  selectedAgentIds: string[],
  unselectedAgentIds: string[],
  campaignIds: string[],
  columns: IAppliedFilters | [],
  totalRecords: number
) => {
  // Case 1: If selected IDs equal total records, send empty arrays
  if (selectedAgentIds.length === totalRecords) {
    return {
      selectedAgentIds: [],
      unselectedAgentIds: [],
      campaignIds,
      columns,
    };
  }

  // Case 2: If selected IDs count is greater than unselected IDs count
  if (
    selectedAgentIds.length > unselectedAgentIds.length &&
    unselectedAgentIds.length + selectedAgentIds.length === totalRecords
  ) {
    return {
      selectedAgentIds: [],
      unselectedAgentIds,
      campaignIds,
      columns,
    };
  }

  // Case 3: If selected IDs count is less than unselected IDs count
  if (
    selectedAgentIds.length <= unselectedAgentIds.length &&
    selectedAgentIds.length !== 0
  ) {
    return {
      selectedAgentIds,
      unselectedAgentIds: [],
      campaignIds,
      columns,
    };
  }

  // Default case: return original arrays
  return {
    selectedAgentIds,
    unselectedAgentIds,
    campaignIds,
    columns,
  };
};
export enum USERGROUP {
  agency = 'AGENCY',
  allocator = 'ALLOCATOR',
  yucollectops = 'YUCOLLECT_OPS',
  admin = 'ADMIN',
}

export const getUserGroup = (userGroup: string): USERGROUP => {
  if (userGroup === 'agency' || userGroup === 'collection_agency')
    return USERGROUP.agency;
  else if (userGroup === 'allocator') return USERGROUP.allocator;
  else if (userGroup === 'yucollect_ops') return USERGROUP.yucollectops;
  return USERGROUP.admin;
};

export const getDayMonthYear = () => {
  const date = new Date();
  return {
    DAY: date.getDate(),
    MONTH: date.getMonth() + 1,
    YEAR: date.getFullYear(),
  };
};
export const isYucollectAdmin = (userGroup: string): boolean => {
  if (userGroup === YUCOLLECT_ADMIN_GROUP) {
    return true;
  }
  return false;
};

export const isAllocator = (userGroup: string): boolean => {
  if (userGroup === ALLOCATOR_GROUP) {
    return true;
  }
  return false;
};

export const isAgency = (userGroup: string): boolean => {
  if (userGroup === 'agency' || userGroup === 'collection_agency') {
    return true;
  }
  return false;
};

export const isAgent = (role: string): boolean => {
  return ['cc_agent'].includes(role);
};

export const isSupervisor = (role: string): boolean => {
  return ['cc_supervisor'].includes(role);
};

export const isAgencyOwner = (role: string): boolean => {
  return ['agency_owner'].includes(role);
};

export const isAgencySupervisor = (role: string): boolean => {
  return ['agency_supervisor'].includes(role);
};

/**
 * Check if an allocation has any IN_PROGRESS group operation jobs
 * @param allocationFileId - The allocation file ID to check
 * @param groupOperationsJobs - Array of group operation jobs from Redux
 * @returns boolean - true if allocation has IN_PROGRESS job
 */
export const hasInProgressGroupOperation = (
  allocationFileId: string,
  groupOperationsJobs: Array<{
    allocationFileId: string;
    status: string;
  }>
): boolean => {
  if (
    !allocationFileId ||
    !groupOperationsJobs ||
    groupOperationsJobs.length === 0
  ) {
    return false;
  }

  return groupOperationsJobs.some(
    (job) =>
      job.allocationFileId === allocationFileId && job.status === 'IN_PROGRESS'
  );
};

export const AVAILABLE_TYPES = [
  'VARCHAR',
  'BIGINT',
  'DOUBLE',
  'TIMESTAMP_NS',
  'BOOLEAN',
  'VARCHAR[]',
  'INTEGER',
  'DECIMAL',
  'string',
  'enum',
  'integer',
  'double',
  'date',
  'boolean',
  'float',
];

export interface TypeCategory {
  baseType: 'string' | 'number' | 'boolean' | 'date' | 'complex';
  originalType: string;
}

export function categorizeType(type: string): TypeCategory {
  if (!AVAILABLE_TYPES.includes(type))
    return { baseType: 'string', originalType: 'VARCHAR' };

  const stringTypes = ['VARCHAR', 'VARCHAR[]', 'string', 'enum'];
  const numberTypes = [
    'BIGINT',
    'DOUBLE',
    'INTEGER',
    'DECIMAL',
    'integer',
    'double',
    'float',
  ];
  const booleanTypes = ['BOOLEAN'];
  const dateTypes = ['TIMESTAMP_NS', 'date'];

  let baseType: TypeCategory['baseType'];

  if (stringTypes.includes(type)) {
    baseType = 'string';
  } else if (numberTypes.includes(type)) {
    baseType = 'number';
  } else if (booleanTypes.includes(type)) {
    baseType = 'boolean';
  } else if (dateTypes.includes(type)) {
    baseType = 'date';
  } else if (type.endsWith('[]')) {
    baseType = 'complex';
  } else {
    baseType = 'number';
  }

  return {
    baseType,
    originalType: type,
  };
}

export function clearAllCookies() {
  Cookies.remove('activeUserGroup');

  // for (const cookieName in cookies) {
  //   Cookies.remove(cookieName);
  // }

  // // Optional: Verify cookies are cleared
  const remainingCookies = Cookies.get();
}

export function encodeParams(
  params:
    | Record<string, string | number | boolean | (string | number | boolean)[]>
    | null
    | undefined
): string {
  if (!params || Object.keys(params).length === 0) {
    return '';
  }

  // createSearchParams handles array values by repeating keys automatically
  const searchParams = createSearchParams(params as URLSearchParamsInit);
  return `?${searchParams.toString()}`;
}
export function decodeParams(
  searchParams: URLSearchParams,
  arrayKeys: string[] = []
): Record<string, string | string[] | null> {
  const decoded: Record<string, string | string[] | null> = {};

  // Get all unique keys from the searchParams
  const allKeys = new Set<string>();
  searchParams.forEach((_, key) => {
    allKeys.add(key);
  });

  allKeys.forEach((key) => {
    if (arrayKeys.includes(key)) {
      // Use getAll() for keys expected to be arrays
      decoded[key] = searchParams.getAll(key);
    } else {
      // Use get() for single-value keys
      decoded[key] = searchParams.get(key);
    }
  });

  // Ensure arrayKeys that were not in the URL are present with an empty array
  arrayKeys.forEach((key) => {
    if (!(key in decoded)) {
      decoded[key] = [];
    }
  });

  return decoded;
}

/**
 * Sanitizes search text by removing special characters that might cause issues
 * @param text The input search text
 * @returns Sanitized search text without special characters like {[}]|":;:
 */
export function sanitizeSearchText(text: string): string {
  return text.replace(/[{}[\]|":;\\]/g, '');
}

/**
 * Checks if the current time is between 7PM (19:00) and 8AM (08:00) in server's local timezone
 * This function uses the local time of the server where it's running
 *
 * @param entityId - The entity ID to check against admin entity IDs
 * @returns {boolean} true if current local time is between 7PM and 8AM, false otherwise
 */
export function disableCallDuringNight(entityId: string): boolean {
  const env = process.env.REACT_APP_ENV || 'qa';

  const YUCOLLECT_ADMIN_ENTITY_IDS = [
    '67e515fc05ea7e004e82f9a6',
    '677f97f091b779003b482d84',
  ];

  // Disable time restriction for qa and stg environments along with yucollect admin entities
  if (env === 'qa' || env === 'stg' || env === 'staging') {
    return false;
  }

  if (YUCOLLECT_ADMIN_ENTITY_IDS.includes(entityId)) {
    return false;
  }

  const now = new Date();

  const currentHour = now.getHours();

  return currentHour >= 19 || currentHour < 8;
}

/**
 * Checks if the current time is between 2PM (14:00) and 8.30AM (08:30) in server's local timezone
 * This function uses the local time of the server where it's running
 *
 * @returns {boolean} true if current local time is between 7PM and 8.30AM, false otherwise
 */
export function enableReportDownloadDuringNight(): boolean {
  const now = new Date();

  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  // Between 2PM (14:00) and 8:30AM (08:30)
  return (
    currentHour >= 14 ||
    currentHour < 8 ||
    (currentHour === 8 && currentMinutes <= 30)
  );
}

/**
 * Checks if navigation should be prevented due to an active call
 * @param isAgentOnline Whether agent is currently on an active call
 * @param showModal Optional callback function to show a modal instead of using message.warning
 * @returns true if navigation should be BLOCKED, false if navigation can proceed
 */
export function preventNavigation(
  isAgentOnline: boolean,
  showModal?: () => void
): boolean {
  if (isAgentOnline) {
    if (showModal) {
      showModal();
    } else {
      message.warning('Cannot navigate while a call is in progress.');
    }
    return true; // Navigation should be blocked
  }
  return false; // Navigation can proceed
}

export function isAllocationExpired(allocationExpiry: string): boolean {
  if (!allocationExpiry) return false;

  let expiryDate: dayjs.Dayjs;

  // Check for DD-MM-YYYY format FIRST to avoid ambiguous parsing
  // (dayjs might interpret "12-11-2025" as MM-DD-YYYY instead of DD-MM-YYYY)
  if (/^\d{2}-\d{2}-\d{4}$/.test(allocationExpiry)) {
    const [day, month, year] = allocationExpiry.split('-');
    expiryDate = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD', true);
  } else {
    // Try to parse other formats (YYYY-MM-DD, ISO strings, etc.)
    expiryDate = dayjs(allocationExpiry);
  }

  // Safety check - if invalid, return false
  if (!expiryDate.isValid()) {
    console.error('Invalid allocation expiry date:', allocationExpiry);
    return false;
  }

  // Format as YYYY-MM-DD to strip time component and normalize timezone differences
  const formattedDate = expiryDate.format('YYYY-MM-DD');

  // Create a new dayjs object from this normalized date string to ensure
  // we're only comparing dates and not times with timezone offsets
  const normalizedDate = dayjs(formattedDate);

  // Compare end of normalized expiry day with current time
  return normalizedDate.endOf('day').isBefore(dayjs());
}
